import { Icon } from '@factorialco/deprecated-design-system'
import {
  PayrollValueObjectsConceptCategoryEnum,
  PayrollValueObjectsConceptUnitTypeEnum,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { Navigation } from 'modules/core/lib/navigation/context'
import { NavigationPaths } from 'modules/core/lib/navigation/context'

export const TABS = {
  information: 'information',
  updates: 'updates',
  tasks: 'tasks',
  messages: 'messages',
  notes: 'notes',
}

interface TabsType {
  id: string
  url: string
  name: string
  additionalInformation?: string
}

export const COMMON_TABS = (
  incidenceId: number,
  isBookkeeper: boolean,
  navigation: Navigation<NavigationPaths>,
  incidenceTypeParams: '' | URLSearchParams | undefined,
  hasInhousePayroll?: boolean,
  hasDevCompensations?: boolean,
  messagesSummary?: string
): TabsType[] => {
  const detailsPayrollBundleRoute = hasDevCompensations
    ? navigation.compensations.employeeUpdates.id.fullPath({ id: incidenceId })
    : navigation.payrollBundle.incidences.id.fullPath({
        id: incidenceId,
      })

  const historyPayrollBundleRoute = hasDevCompensations
    ? navigation.compensations.employeeUpdates.idChanges.fullPath({ id: incidenceId })
    : navigation.payrollBundle.incidences.idChanges.fullPath({
        id: incidenceId,
      })

  const messagesPayrollBundleRoute = hasDevCompensations
    ? navigation.compensations.employeeUpdates.idMessages.fullPath({ id: incidenceId })
    : navigation.payrollBundle.incidences.idMessages.fullPath({
        id: incidenceId,
      })

  const detailBookkeperRoute = incidenceTypeParams
    ? `${navigation.bookkeepers.incidences.details.fullPath({
        id: incidenceId,
      })}?${incidenceTypeParams}`
    : navigation.bookkeepers.incidences.details.fullPath({ id: incidenceId })

  const historyBookkeeperRoute = incidenceTypeParams
    ? `${navigation.bookkeepers.incidences.idChanges.fullPath({
        id: incidenceId,
      })}?${incidenceTypeParams}`
    : navigation.bookkeepers.incidences.idChanges.fullPath({ id: incidenceId })

  const messagesBookkeeperRoute = incidenceTypeParams
    ? `${navigation.bookkeepers.incidences.idMessages.fullPath({
        id: incidenceId,
      })}?${incidenceTypeParams}`
    : navigation.bookkeepers.incidences.idMessages.fullPath({ id: incidenceId })

  const tabsList: TabsType[] = [
    {
      id: TABS.information,
      url: isBookkeeper ? detailBookkeperRoute : detailsPayrollBundleRoute,
      name: i18n.t('bookkeepers.detail_tabs.information'),
    },
    {
      id: TABS.updates,
      url: isBookkeeper ? historyBookkeeperRoute : historyPayrollBundleRoute,
      name: i18n.t('bookkeepers.detail_tabs.updates'),
    },
  ]

  if (!hasInhousePayroll || isBookkeeper) {
    tabsList.push({
      id: TABS.messages,
      url: isBookkeeper ? messagesBookkeeperRoute : messagesPayrollBundleRoute,
      name: i18n.t('bookkeepers.detail_tabs.messages'),
      additionalInformation: messagesSummary,
    })
  }

  if (hasInhousePayroll && !isBookkeeper) {
    tabsList.push({
      id: TABS.notes,
      url: hasDevCompensations
        ? navigation.compensations.employeeUpdates.idNotes.fullPath({ id: incidenceId })
        : navigation.payrollBundle.incidences.idNotes.fullPath({
            id: incidenceId,
          }),
      name: i18n.t('bookkeepers.detail_tabs.notes'),
    })
  }

  return tabsList
}

export const DEFAULT_SORT_ATTRIBUTE = 'name'
export const DEFAULT_SORT_DIRECTION = 'asc'
export const INITIAL_PAGE = 1
export const PAGINATION_PER_PAGE = 50
export const PAGINATION_PER_PAGE_SUPPLEMENTS = 25

export const TRANSITION_DURATION_FASTER = 0.6

// Dispath actions
export const UPDATE_DATA = 'UPDATE_DATA'
export const ADD_SUPPLEMENT_TYPE = 'ADD_SUPPLEMENT_TYPE'
export const UPDATE_SUPPLEMENT_TYPE = 'UPDATE_SUPPLEMENT_TYPE'
export const DELETE_SUPPLEMENT_TYPE = 'DELETE_SUPPLEMENT_TYPE'
export const RESET_DATA = 'RESET_DATA'
export const SHOW_UPLOAD_MODAL = 'SHOW_UPLOAD_MODAL'
export const SHOW_ASSIGN_TAXONOMY_MODAL = 'SHOW_ASSIGN_TAXONOMY_MODAL'
export const SHOW_DELETE_MODAL = 'SHOW_DELETE_MODAL'
export const SHOW_MANAGED_IMPORTED_MODAL = 'SHOW_MANAGED_IMPORTED_MODAL'
export const SHOW_RESULTS_FILES_MODAL = 'SHOW_RESULTS_FILES_MODAL'

// Worksheet matching constants
export const MATCH_ROW_INDEX = 0
export const MATCH_COL_INDEX = 1

// Worksheet max rows and columns
export const MATCH_MAX_ROWS = 200
export const MATCH_MAX_COLUMNS = 200

// Upload stages SEE PayrollResultStagesKey
export const UPLOAD_FILE = 'UPLOAD_FILE' // Should be unified with SELECT_DATA used to be a thing before dev_payroll_results_review
export const SELECT_DATA = 'SELECT_DATA'
export const MAP_TO_CONCEPTS = 'MAP_TO_CONCEPTS'
export const REVIEW_AND_IMPORT = 'REVIEW_AND_IMPORT'
// One of the payments stage
export const LIST_PAYMENTS = 'LIST_PAYMENT'

// Assign modal types
export const PAYROLL_RESULT = 'PAYROLL_RESULT'
export const BULK_IMPORT_SUPPLEMENT = 'BULK_IMPORT_SUPPLEMENT'

// Policy period status
export const PAYROLL_CALCULATION = 'payroll_calculation'
export const PAID = 'paid'

// Payment Status
export const NOT_PAID = 'not_paid'
export const PARTIALLY_PAID = 'partially_paid'
export const OVERPAID = 'overpaid'

// Import results row status
export const NEW = 'NEW'
export const ERROR = 'ERROR'
export const UNCHANGED = 'UNCHANGED'
export const UPDATED = 'UPDATED'

// Generate Payment status
export const SUCCESS = 'SUCCESS'

// Payroll issues
export const ADDITIONAL_COMPENSATION_ISSUE = 'additional_compensation_issue'
export const INVALID_BANK_ACCOUNT_NUMBER = 'invalid_bank_account_number'
export const NO_BANK_ACCOUNT_NUMBER = 'no_bank_account_number'
export const PAYROLL_INTEGRATIONS_KOMBO_AUTHORIZATION_ISSUE =
  'payroll_integrations_kombo_authorization_issue'
export const PAYROLL_INTEGRATIONS_KOMBO_MISSING_CONTRACT_TAXONOMY_CODE_ISSUE =
  'payroll_integrations_kombo_missing_contract_taxonomy_code_issue'
export const PAYROLL_INTEGRATIONS_KOMBO_MISSING_EMPLOYEE_CODE_ISSUE =
  'payroll_integrations_kombo_missing_employee_code_issue'
export const SUPPLEMENT_ISSUE = 'supplement_issue'

/**
 * We need some default concepts names to be able to identify them
 * This should be generated from the backend in order to prevent inconsistencies
 */
export enum DEFAULT_RESULT_CONCEPT {
  gross_pay = 'gross_pay',
  net_pay = 'net_pay',
  company_cost = 'company_cost',
  base_salary = 'base_salary',
  expenses = 'expenses',
  salary_advance = 'salary_advance',
  es_a_cta_convenio = 'es_a_cta_convenio',
  es_additional_overtime = 'es_additional_overtime',
  es_at_ep_company = 'es_at_ep_company',
  es_at_ep_base = 'es_at_ep_base',
  es_common_c_company = 'es_common_c_company',
  es_common_c_base = 'es_common_c_base',
  es_common_d_company = 'es_common_d_company',
  es_common_c_employee = 'es_common_c_employee',
  es_deduction_compensation_in_kind = 'es_deduction_compensation_in_kind',
  es_dismissal_compensation = 'es_dismissal_compensation',
  es_extraordinary_compensation = 'es_extraordinary_compensation',
  es_incentives = 'es_incentives',
  es_income_tax = 'es_income_tax',
  es_overtime_complementary_hours = 'es_overtime_complementary_hours',
  es_overtime_extra_hours = 'es_overtime_extra_hours',
  es_overtime_deduction = 'es_overtime_deduction',
  es_labour_agreement_plus = 'es_labour_agreement_plus',
  es_position_plus = 'es_position_plus',
  es_prorate_extra_payments = 'es_prorate_extra_payments',
  es_ss_benefits_and_compensations = 'es_ss_benefits_and_compensations',
  es_seniority_plus = 'es_seniority_plus',
  es_ss_company = 'es_ss_company',
  es_ss_employee = 'es_ss_employee',
  es_ss_total = 'es_ss_total',
  es_tax_base = 'es_tax_base',
  es_total_deductions = 'es_total_deductions',
  es_training_employee = 'es_training_employee',
  es_training_company = 'es_training_company',
  es_training_base = 'es_training_base',
  es_unemployement_employee = 'es_unemployement_employee',
  es_unemployement_company = 'es_unemployement_company',
  es_unmployement_base = 'es_unmployement_base',
  es_wage_guarantee_fund = 'es_wage_guarantee_fund',
  es_wage_guarantee_fund_base = 'es_wage_guarantee_fund_base',
  fr_average_hourly_rate = 'fr_average_hourly_rate',
  fr_food_allowance = 'fr_food_allowance',
  fr_hours_worked = 'fr_hours_worked',
  fr_social_package = 'fr_social_package',
  fr_transportation_allowance = 'fr_transportation_allowance',
  fr_employer_cost = 'fr_employer_cost',
  fr_net_social_amount = 'fr_net_social_amount',
  fr_net_taxable = 'fr_net_taxable',
  fr_withholding_tax_amount = 'fr_withholding_tax_amount',
  comission = 'comission',
  hazard_allowance = 'hazard_allowance',
  night_shift_allowance = 'night_shift_allowance',
  holiday_allowance = 'holiday_allowance',
  attendance_allowance = 'attendance_allowance',
  work_from_home_allowance = 'work_from_home_allowance',
  responsability_allowance = 'responsability_allowance',
  meal_benefit = 'meal_benefit',
  transport_benefit = 'transport_benefit',
  childcare_benefit = 'childcare_benefit',
  late_payment = 'late_payment',
  salary_advancement = 'salary_advancement',
  guaranteed_minimum_salary = 'guaranteed_minimum_salary',
  wage_garnishment = 'wage_garnishment',
  net_salary_adjustment = 'net_salary_adjustment',
  overtime_contribution_base = 'overtime_contribution_base',
}

export enum ResultConcepts {
  regular_payroll = 'regular_payroll',
  severance = 'severance',
  extra_pay = 'extra_pay',
  delayed_payment = 'delayed_payment',
  bonus_benefit = 'bonus_benefit',
  salary_advance = 'salary_advance',
  rate_difference = 'rate_difference',
}

export type ResultConceptsType = keyof typeof ResultConcepts

export const isResultConceptsType = (value: string): value is ResultConceptsType => {
  return Object.keys(ResultConcepts).includes(value)
}

export const resultConceptsLabels = (): Record<ResultConceptsType, string> =>
  ({
    regular_payroll: i18n.t('payroll_bundle.results_payments.results.concepts.regular_payroll'),
    severance: i18n.t('payroll_bundle.results_payments.results.concepts.severance'),
    extra_pay: i18n.t('payroll_bundle.results_payments.results.concepts.extra_pay'),
    delayed_payment: i18n.t('payroll_bundle.results_payments.results.concepts.delayed_payment'),
    bonus_benefit: i18n.t('payroll_bundle.results_payments.results.concepts.bonus_benefit'),
    salary_advance: i18n.t('payroll_bundle.results_payments.results.concepts.salary_advance'),
    rate_difference: i18n.t('payroll_bundle.results_payments.results.concepts.rate_difference'),
  } as const)

export type ResultConceptsLabelsType = keyof typeof resultConceptsLabels

const PAYROLL_CONCEPT_UNIT_TYPES: PayrollValueObjectsConceptUnitTypeEnum[] = [
  'money',
  'distance',
  'time',
  'unit',
]

export const PAYROLL_CONCEPT_CATEGORIES: PayrollValueObjectsConceptCategoryEnum[] = [
  'earnings_fixed_salary',
  'earnings_benefits_in_kind',
  'earnings_others',
  'earnings_variable',
  'deductions',
  'company_contribution',
  'summarized_values',
]

const payrollConceptUnitTypesLabels = (): Record<PayrollValueObjectsConceptUnitTypeEnum, string> =>
  ({
    money: i18n.t('payroll_bundle.concept_unit_type.money'),
    distance: i18n.t('payroll_bundle.concept_unit_type.distance'),
    time: i18n.t('payroll_bundle.concept_unit_type.time'),
    unit: i18n.t('payroll_bundle.concept_unit_type.unit'),
  } as const)

export const payrollConceptUnitTypeOptions = () =>
  PAYROLL_CONCEPT_UNIT_TYPES.map((value) => ({
    label: payrollConceptUnitTypesLabels()[value],
    value,
  }))

export const payrollConceptCategoriesLabels = (): Record<
  PayrollValueObjectsConceptCategoryEnum,
  string
> =>
  ({
    earnings_fixed_salary: i18n.t('payroll_bundle.concept_category.earnings_fixed_salary'),
    earnings_benefits_in_kind: i18n.t('payroll_bundle.concept_category.earnings_benefits_in_kind'),
    earnings_others: i18n.t('payroll_bundle.concept_category.earnings_others'),
    earnings_variable: i18n.t('payroll_bundle.concept_category.earnings_variable'),
    deductions: i18n.t('payroll_bundle.concept_category.deductions'),
    company_contribution: i18n.t('payroll_bundle.concept_category.company_contribution'),
    summarized_values: i18n.t('payroll_bundle.concept_category.summarized_values'),
  } as const)

export const payrollConceptCategoriesOptions = () =>
  PAYROLL_CONCEPT_CATEGORIES.map((value) => ({
    label: payrollConceptCategoriesLabels()[value],
    value,
  }))

export const RESULT_ACCEPTED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/vnd.ms-excel',
]

export enum ResultValidationStatus {
  valid = 'valid',
  warning = 'warning',
  invalid = 'invalid',
}

export type ResultValidationStatusType = keyof typeof ResultValidationStatus

export const isResultValidationStatus = (value: string): value is ResultValidationStatusType => {
  return Object.keys(ResultValidationStatus).includes(value)
}

export interface Option {
  title: string
  onClick: () => void
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  danger?: boolean
}

export const ATTENDANCE_COMPENSATION_SOURCE_TYPE = 'Attendance::Compensation'
export const TIMEBLOCK_SOURCE_TYPE = 'TimeSettings::CustomTimeRangeCategory'

/**
 * The supplements table loads the latest contracts defined in the pay period.
 * This can end up filtering out recently terminated employees that need a final
 * severance payment in the following period. This constant extends the window.
 */
export const RECENT_CONTRACT_GRACE_PERIOD_DAYS = 90

/**
 * Rank flags are part of the sorting logic for the supplements table, these constants are used to sort
 * employees with supplements that are not filled (draft)
 * or missing something using a rank system of bitwise operations
 */
export const RANKS_HAS_DRAFT_SUPPLEMENTS = 1 << 1 // 2 in decimal
export const RANK_HAS_SUPPLEMENTS = 1 // 1

export const DEFAULT_CONCEPT_UNIT_TYPE = 'money'
export const DEFAULT_CONCEPT_CATEGORY = 'earnings_fixed_salary'
